import { zxcvbn } from '@zxcvbn-ts/core'

/**
 * パスワード強度メーターのクラス
 * パスワードの複雑さをリアルタイムで評価し、視覚的にフィードバックを提供する
 */
class PasswordStrengthMeter {
  constructor(passwordInput, meterContainer) {
    this.passwordInput = passwordInput
    this.meterContainer = meterContainer
    this.strengthLevels = ['非常に弱い', '弱い', '中', '強い', '非常に強い']
    this.strengthColors = ['#e53935', '#fb8c00', '#fbc02d', '#8bc34a', '#388e3c']
    this.init()
  }

  /**
   * メーターの初期化
   */
  init() {
    this.passwordInput.addEventListener('input', (e) => {
      try {
        this.updateMeter(e.target.value)
      } catch (error) {
        console.warn('Password strength meter error:', error)
      }
    })

    this.initFieldValidation()

    try {
      this.showMeter()
      this.updateMeter('')
      this.updateSubmitButtonState([false, false, false, false])
    } catch (error) {
      console.warn('Password strength meter initialization error:', error)
    }

    this.initPasswordToggle()
  }

  /**
   * フィールドバリデーションの初期化
   * パスワード確認フィールドと現在のパスワードフィールドの入力監視を設定
   */
  initFieldValidation() {
    const passwordForm = this.passwordInput.closest('form')
    if (!passwordForm) return

    const confirmationField = passwordForm.querySelector('input[name*="confirmation"]')
    if (confirmationField) {
      confirmationField.addEventListener('input', () => {
        const requirements = this.checkRequirements(this.passwordInput.value)
        this.updateSubmitButtonState(requirements)
      })
    }

    const currentPasswordField = passwordForm.querySelector('input[name*="current"]')
    if (currentPasswordField) {
      currentPasswordField.addEventListener('input', () => {
        const requirements = this.checkRequirements(this.passwordInput.value)
        this.updateSubmitButtonState(requirements)
      })
    }
  }

  /**
   * パスワード表示切り替え機能の初期化
   */
  initPasswordToggle() {
    const toggleButtons = document.querySelectorAll('.password-toggle')

    toggleButtons.forEach((button) => {
      if (button.dataset.initialized === 'true') return
      button.addEventListener('click', (e) => this.handlePasswordToggle(e))
      button.dataset.initialized = 'true'
    })
  }

  /**
   * パスワード表示切り替えのハンドラー
   */
  handlePasswordToggle(e) {
    e.preventDefault()
    e.stopPropagation()

    const button = e.currentTarget
    const container = button.closest('.password-input-container, .form-group, .input-group, form, div')
    if (!container) return

    const passwordField = container.querySelector('input[type="password"], input[type="text"].password-field')
    if (!passwordField) return

    const isHidden = passwordField.type === 'password'
    passwordField.type = isHidden ? 'text' : 'password'

    this.updateToggleIcon(button, isHidden)
  }

  /**
   * トグルアイコンを更新
   */
  updateToggleIcon(button, isShowingPassword) {
    const icon = button.querySelector('i, .fas, [class*="fa-"], svg')

    if (icon) {
      const iconClass = isShowingPassword ? 'fas fa-eye-slash' : 'fas fa-eye'

      if (icon.tagName === 'SVG' || icon instanceof SVGElement) {
        const newIcon = document.createElement('i')
        newIcon.className = iconClass
        icon.parentNode.replaceChild(newIcon, icon)
      } else {
        icon.className = iconClass
      }

      button.setAttribute('aria-label', isShowingPassword ? 'パスワードを隠す' : 'パスワードを表示')
    }
  }

  /**
   * パスワードメーターのHTML表示
   */
  showMeter() {
    this.meterContainer.innerHTML = this.generateMeterHTML()
    this.meterContainer.style.display = 'block'
  }

  /**
   * メーターのHTML生成
   */
  generateMeterHTML() {
    return `
      <ul class="password-requirements">
        <li class="requirement-item requirement-8chars">
          <span class="requirement-icon">✓</span>
          <span class="requirement-text">8文字以上</span>
        </li>
        <li class="requirement-item requirement-letters">
          <span class="requirement-icon">✓</span>
          <span class="requirement-text">英字を含む</span>
        </li>
        <li class="requirement-item requirement-numbers">
          <span class="requirement-icon">✓</span>
          <span class="requirement-text">数字を含む</span>
        </li>
        <li class="requirement-item requirement-symbols">
          <span class="requirement-icon">✓</span>
          <span class="requirement-text">記号を含む</span>
        </li>
      </ul>
      <div class="password-strength-info">
        <div class="password-strength-text">このパスワードの強度：</div>
        <div class="password-strength-meter">
          ${Array.from({ length: 5 }, (_, i) => `<div class="strength-bar" data-bar="${i}"></div>`).join('')}
        </div>
      </div>
    `
  }

  /**
   * パスワードメーターの更新
   */
  updateMeter(password) {
    try {
      const requirements = this.checkRequirements(password)
      this.updateRequirementItems(requirements)
      this.updateStrengthDisplay(password)
      this.updateSubmitButtonState(requirements)
    } catch (error) {
      console.warn('Password strength update error:', error)
    }
  }

  /**
   * 要件アイテムの表示更新
   */
  updateRequirementItems(requirements) {
    const requirementItems = this.meterContainer.querySelectorAll('.requirement-item')

    requirementItems.forEach((item, index) => {
      const icon = item.querySelector('.requirement-icon')
      const isValid = requirements[index]

      item.classList.toggle('valid', isValid)
      icon.classList.toggle('valid', isValid)
    })
  }

  /**
   * 強度表示の更新
   */
  updateStrengthDisplay(password) {
    const strengthTextElement = this.meterContainer.querySelector('.password-strength-text')
    const strengthBars = this.meterContainer.querySelectorAll('.strength-bar')

    if (password && password.length > 0) {
      const result = zxcvbn(password)
      this.updateStrengthText(strengthTextElement, result.score)
      this.updateStrengthBars(strengthBars, result.score)
    } else {
      this.resetStrengthDisplay(strengthTextElement, strengthBars)
    }
  }

  /**
   * 強度テキストの更新
   */
  updateStrengthText(element, score) {
    const strengthLevel = this.strengthLevels[score]
    element.innerHTML = `このパスワードの強度：<span class="strength-level strength-${score}">${strengthLevel}</span>`
    element.className = 'password-strength-text'
  }

  /**
   * 強度バーの更新
   */
  updateStrengthBars(bars, score) {
    const activeColor = this.strengthColors[score]

    bars.forEach((bar, index) => {
      bar.style.backgroundColor = index <= score ? activeColor : '#e0e0e0'
    })
  }

  /**
   * 強度表示のリセット
   */
  resetStrengthDisplay(textElement, bars) {
    textElement.innerHTML = 'このパスワードの強度：<span class="strength-level"></span>'
    textElement.className = 'password-strength-text'

    bars.forEach(bar => {
      bar.style.backgroundColor = '#e0e0e0'
    })
  }

  /**
   * パスワード要件のチェック
   * Note: PasswordValidationService側と同期させる
   *
   * @param {string} password - チェック対象のパスワード
   * @returns {boolean[]} 各要件の満足状況
   */
  checkRequirements(password) {
    return [
      password.length >= 8,
      /[a-zA-Z]/.test(password),
      /[0-9]/.test(password),
      /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)
    ]
  }

  /**
   * メーターを非表示にする
   */
  hideMeter() {
    this.meterContainer.style.display = 'none'
  }

  /**
   * 送信ボタンの状態更新
   * パスワード要件、確認パスワード、現在のパスワードの状態に基づいてボタンを有効/無効化
   */
  updateSubmitButtonState(requirements) {
    const validationState = this.getFormValidationState(requirements)
    const passwordForm = this.passwordInput.closest('form')
    const targetButton = passwordForm ? passwordForm.querySelector('input[type="submit"]') : null

    if (targetButton) {
      this.toggleButtonState(targetButton, validationState.shouldEnable)
    }
  }

  /**
   * フォームのバリデーション状態を取得
   */
  getFormValidationState(requirements) {
    const isPasswordValid = requirements.every(requirement => requirement)
    const passwordForm = this.passwordInput.closest('form')

    let passwordsMatch = true
    let currentPasswordFilled = true
    const newPasswordFilled = this.passwordInput.value.trim().length > 0

    if (passwordForm) {
      const confirmationField = passwordForm.querySelector('input[name*="confirmation"]')
      if (confirmationField) {
        passwordsMatch = this.passwordInput.value === confirmationField.value
      }

      const currentPasswordField = passwordForm.querySelector('input[name*="current"]')
      if (currentPasswordField) {
        currentPasswordFilled = currentPasswordField.value.trim().length > 0
      }
    }

    return {
      shouldEnable: isPasswordValid && passwordsMatch && currentPasswordFilled && newPasswordFilled,
      isPasswordValid,
      passwordsMatch,
      currentPasswordFilled,
      newPasswordFilled
    }
  }

  /**
   * ボタンの有効/無効状態を切り替え
   */
  toggleButtonState(button, shouldEnable) {
    if (shouldEnable) {
      button.disabled = false
      button.classList.remove('disabled')
      button.style.opacity = '1'
      button.style.cursor = 'pointer'
    } else {
      button.disabled = true
      button.classList.add('disabled')
      button.style.opacity = '0.6'
      button.style.cursor = 'not-allowed'
    }
  }
}

export default PasswordStrengthMeter
